import React, {
    useMemo,
    useCallback,
} from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'shared/hooks/useSafeTranslation'

import useStyle from 'shared/hooks/useStyle'
import StepPageLayout from 'shared/StepPageLayout'
import NestedStep, { useNestedStep } from 'shared/NestedStep'
import SummaryChoicesList from 'shared/SummaryChoicesList'
import { StepSingleSelectPropTypes } from 'shared/utils/stepsUtils'

import ChoiceButton from './ChoiceButton'
import styles from './YesNoStep.style'

const YES_IDENTIFIER = 'YES'
const NO_IDENTIFIER = 'NO'

const propTypes = {
    step: StepSingleSelectPropTypes.isRequired,
    inputCallback: PropTypes.func,
    assetNumber: PropTypes.string.isRequired,
    curentGroupSteps: PropTypes.arrayOf(
        PropTypes.shape({ stepName: PropTypes.string.isRequired }),
    ).isRequired,
    isNested: PropTypes.bool,
    isSummaryView: PropTypes.bool,
    nestedInputFromUserInput: PropTypes.func,
}

const YesNoStep = ({
    step,
    inputCallback,
    assetNumber,
    curentGroupSteps,
    isNested = false,
    isSummaryView = false,
    nestedInputFromUserInput,
}) => {
    const { t } = useTranslation()
    const classes = useStyle(styles)

    const {
        choice,
        isChanged,
        onChange,
    } = useNestedStep({
        step,
        curentGroupSteps,
        inputCallback,
        isNested,
        isSummaryView,
        nestedInputFromUserInput,
    })

    const onChoice = useCallback((event) => {
        const value = event.currentTarget.name

        onChange(value)
    }, [onChange])

    const yesChoice = useMemo(() => {
        return step.choices.find((s) => {
            return s.choiceIdentifier === YES_IDENTIFIER
        })
    }, [step.choices])
    const noChoice = useMemo(() => {
        return step.choices.find((s) => {
            return s.choiceIdentifier === NO_IDENTIFIER
        })
    }, [step.choices])

    const buttonsRender = () => {
        return (
            <>
                <div css={classes.buttons}>
                    <ChoiceButton
                        choice={yesChoice}
                        onChoice={onChoice}
                        selected={choice ? choice === yesChoice.choiceIdentifier : null}
                        testId="yes-button"
                    />
                    <ChoiceButton
                        choice={noChoice}
                        onChoice={onChoice}
                        selected={choice ? choice === noChoice.choiceIdentifier : null}
                        testId="no-button"
                    />
                </div>
                <React.Fragment key={`${choice}-child`}>
                    <NestedStep
                        step={step}
                        choice={choice}
                        assetNumber={assetNumber}
                        inputCallback={inputCallback}
                        curentGroupSteps={curentGroupSteps}
                        isChanged={isChanged}
                        nestedInputFromUserInput={nestedInputFromUserInput}
                    />
                </React.Fragment>
            </>
        )
    }

    const buttons = useMemo(() => {
        return [
            yesChoice,
            noChoice,
        ]
    }, [
        yesChoice,
        noChoice,
    ])

    return (
        <StepPageLayout
            title={t(`processDefinition:${step.stepTitle}`)}
            isRequired={step.isRequired}
            isAnswered={!!choice}
            isNested={isNested}
            isSummaryView={isSummaryView}
        >
            <div css={classes.form}>
                {isSummaryView ? (
                    <SummaryChoicesList
                        items={buttons}
                        choice={choice}
                        isAnswered={!!choice}
                        step={step}
                        assetNumber={assetNumber}
                        curentGroupSteps={curentGroupSteps}
                        isSummaryView={isSummaryView}
                    />
                ) : buttonsRender()}
            </div>
        </StepPageLayout>
    )
}

YesNoStep.propTypes = propTypes

export default YesNoStep
